import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import { Select, Autocomplete, TextInput, Input } from '@mantine/core';
import { SEARCH_DEBOUNCE_DELAY } from '@/constants';
import { useDebounce } from '@/hooks';
import { DateInput } from '@mantine/dates';
import {
  IconCalendar,
  IconMapPin,
  IconSearch,
  IconEye,
} from '@tabler/icons-react';
import { useShallow } from 'zustand/react/shallow';
import { useTranslation } from 'react-i18next';
import { useGeoMarkupStore } from '@/store';
import {
  selectHasActiveNoteFilters,
  selectHasActiveNoteFiltersInMapPanel,
  selectUniqueNoteLocations,
} from '@/store/selectors/noteSelectors';
import { NOTE_COLOR_SWATCHES, NOTE_VISIBILITY_ALL } from '@/constants/notes';
import { ICON_SIZE_SM } from '@/constants/ui';
import { ColorSwatchToggleGroup } from '@/components/common/ColorSwatchToggleGroup';
import { Z_INDEX_MOBILE_MODAL_DATEPICKER } from '@/constants/zIndex';
import { getDateInputPattern } from '@/config/dateLocales';
import { ClearFiltersButton } from '@/components/ui/ClearFiltersButton';
import { SortControls } from '@/components/ui/SortControls';
import { MemberFilterMultiSelect } from '@/components/common/MemberFilterMultiSelect';
import { NOTE_SORT_OPTIONS } from '@/constants/sortOptions';
import type { NoteSortField } from '@/store/slices/noteFiltersSlice';
import { NoteVisibility } from '@/types/notes';
import styles from './NoteFilters.module.css';

interface NoteFiltersProps {
  readonly showSearch?: boolean;
  readonly showSort?: boolean;
}

export const NoteFilters = memo(function NoteFilters({
  showSearch = false,
  showSort = true,
}: NoteFiltersProps) {
  const { t, i18n } = useTranslation();

  // The map side panel renders without the search field (search lives in the
  // floating map SearchBar). `!showSearch` is the established map-panel signal.
  const isMapPanel = !showSearch;

  const selectedColors = useGeoMarkupStore(
    useShallow((state) => state.noteFilters.colors)
  );
  const visibility = useGeoMarkupStore((state) => state.noteFilters.visibility);
  const search = useGeoMarkupStore((state) => state.noteFilters.search);
  const authors = useGeoMarkupStore(
    useShallow((state) => state.noteFilters.authors)
  );
  const hasLocation = useGeoMarkupStore(
    (state) => state.noteFilters.hasLocation
  );
  const locationFilter = useGeoMarkupStore(
    (state) => state.noteFilters.location
  );
  const uniqueNoteLocations = useGeoMarkupStore(
    useShallow(selectUniqueNoteLocations)
  );
  const dateFrom = useGeoMarkupStore((state) => state.noteFilters.dateFrom);
  const dateTo = useGeoMarkupStore((state) => state.noteFilters.dateTo);
  const hasActiveFilters = useGeoMarkupStore(
    isMapPanel
      ? selectHasActiveNoteFiltersInMapPanel
      : selectHasActiveNoteFilters
  );
  const toggleColor = useGeoMarkupStore(
    (state) => state.noteFilters.toggleColor
  );
  const setVisibility = useGeoMarkupStore(
    (state) => state.noteFilters.setVisibility
  );
  const setSearch = useGeoMarkupStore((state) => state.noteFilters.setSearch);
  const setAuthors = useGeoMarkupStore((state) => state.noteFilters.setAuthors);
  const setHasLocation = useGeoMarkupStore(
    (state) => state.noteFilters.setHasLocation
  );
  const setLocation = useGeoMarkupStore(
    (state) => state.noteFilters.setLocation
  );
  const setDateRange = useGeoMarkupStore(
    (state) => state.noteFilters.setDateRange
  );
  const resetFilters = useGeoMarkupStore(
    (state) => state.noteFilters.resetFilters
  );
  const sortBy = useGeoMarkupStore((state) => state.noteFilters.sortBy);
  const sortDescending = useGeoMarkupStore(
    (state) => state.noteFilters.sortDescending
  );
  const setSortBy = useGeoMarkupStore((state) => state.noteFilters.setSortBy);
  const setSortDescending = useGeoMarkupStore(
    (state) => state.noteFilters.setSortDescending
  );

  const handleSortByChange = useCallback(
    (value: string) => setSortBy(value as NoteSortField),
    [setSortBy]
  );

  // Search debounce
  const [localSearch, setLocalSearch] = useState(search);
  const { debouncedValue: debouncedSearch } = useDebounce(
    localSearch,
    SEARCH_DEBOUNCE_DELAY
  );
  const isExternalSearchChange = useRef(false);

  useEffect(() => {
    isExternalSearchChange.current = true;
    setLocalSearch(search);
  }, [search]);

  useEffect(() => {
    if (isExternalSearchChange.current) {
      isExternalSearchChange.current = false;
      return;
    }
    if (debouncedSearch !== search) {
      setSearch(debouncedSearch);
    }
  }, [debouncedSearch, search, setSearch]);

  const handleSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setLocalSearch(event.currentTarget.value);
    },
    []
  );

  // Location debounce
  const [localLocation, setLocalLocation] = useState(locationFilter);
  const { debouncedValue: debouncedLocation } = useDebounce(
    localLocation,
    SEARCH_DEBOUNCE_DELAY
  );
  const isExternalLocationChange = useRef(false);

  useEffect(() => {
    isExternalLocationChange.current = true;
    setLocalLocation(locationFilter);
  }, [locationFilter]);

  useEffect(() => {
    if (isExternalLocationChange.current) {
      isExternalLocationChange.current = false;
      return;
    }
    if (hasLocation === true && debouncedLocation !== locationFilter) {
      setLocation(debouncedLocation);
    }
  }, [debouncedLocation, locationFilter, setLocation, hasLocation]);

  const dateFormat = useMemo(
    () => getDateInputPattern(i18n.language).pattern,
    [i18n.language]
  );

  const locationOptions = useMemo(
    () => [
      { value: 'all', label: t('notes.filters.locationAll') },
      { value: 'with', label: t('notes.filters.withLocation') },
      { value: 'without', label: t('notes.filters.withoutLocation') },
    ],
    [t]
  );

  const visibilityOptions = useMemo(
    () => [
      { value: NOTE_VISIBILITY_ALL, label: t('notes.filters.visibility.all') },
      {
        value: NoteVisibility.Private,
        label: t('notes.filters.visibility.private'),
      },
      {
        value: NoteVisibility.Workspace,
        label: t('notes.filters.visibility.workspace'),
      },
    ],
    [t]
  );

  const handleVisibilityChange = useCallback(
    (value: string | null) => {
      if (
        value === NoteVisibility.Private ||
        value === NoteVisibility.Workspace
      ) {
        setVisibility(value);
        return;
      }
      setVisibility(null);
    },
    [setVisibility]
  );

  const visibilityValue = visibility ?? NOTE_VISIBILITY_ALL;

  const handleLocationChange = useCallback(
    (value: string | null) => {
      if (value === 'with') {
        setHasLocation(true);
        return;
      }
      setHasLocation(value === 'without' ? false : null);
    },
    [setHasLocation]
  );

  const handleLocationFilterChange = useCallback((value: string) => {
    setLocalLocation(value);
  }, []);

  const handleDateFromChange = useCallback(
    (value: string | Date | null) => {
      if (value === null) {
        setDateRange(null, dateTo);
      } else if (typeof value === 'string') {
        const parsed = new Date(value);
        setDateRange(isNaN(parsed.getTime()) ? null : parsed, dateTo);
      } else {
        setDateRange(value, dateTo);
      }
    },
    [setDateRange, dateTo]
  );

  const handleDateToChange = useCallback(
    (value: string | Date | null) => {
      if (value === null) {
        setDateRange(dateFrom, null);
      } else if (typeof value === 'string') {
        const parsed = new Date(value);
        setDateRange(dateFrom, isNaN(parsed.getTime()) ? null : parsed);
      } else {
        setDateRange(dateFrom, value);
      }
    },
    [setDateRange, dateFrom]
  );

  const locationValue =
    hasLocation === null ? 'all' : hasLocation ? 'with' : 'without';

  return (
    <div className={styles.container}>
      {/* Row 1: Color swatches */}
      <Input.Wrapper label={t('notes.filters.color')}>
        <ColorSwatchToggleGroup
          colors={NOTE_COLOR_SWATCHES}
          selected={selectedColors}
          onToggle={toggleColor}
          withLabelOffset
        />
      </Input.Wrapper>

      {/* Row 2: All other filters — flex-wrap layout */}
      <div className={styles.fieldsRow}>
        {showSearch && (
          <TextInput
            className={styles.fieldWide}
            label={t('notes.filters.title')}
            placeholder={t('notes.filters.titlePlaceholder')}
            leftSection={<IconSearch size={ICON_SIZE_SM} />}
            value={localSearch}
            onChange={handleSearchChange}
          />
        )}

        <Select
          className={styles.fieldWide}
          label={t('notes.filters.visibilityLabel')}
          data={visibilityOptions}
          value={visibilityValue}
          onChange={handleVisibilityChange}
          leftSection={<IconEye size={ICON_SIZE_SM} />}
          allowDeselect={false}
        />

        <MemberFilterMultiSelect
          className={styles.fieldWide}
          label={t('notes.filters.author')}
          placeholder={t('notes.filters.authorPlaceholder')}
          value={authors}
          onChange={setAuthors}
        />

        <DateInput
          className={styles.dateField}
          label={t('notes.filters.dateFrom')}
          placeholder={dateFormat}
          valueFormat={dateFormat}
          value={dateFrom}
          onChange={handleDateFromChange}
          leftSection={<IconCalendar size={ICON_SIZE_SM} />}
          clearable
          maxDate={dateTo ?? undefined}
          popoverProps={{
            zIndex: Z_INDEX_MOBILE_MODAL_DATEPICKER,
            withinPortal: true,
          }}
        />

        <DateInput
          className={styles.dateField}
          label={t('notes.filters.dateTo')}
          placeholder={dateFormat}
          valueFormat={dateFormat}
          value={dateTo}
          onChange={handleDateToChange}
          leftSection={<IconCalendar size={ICON_SIZE_SM} />}
          clearable
          minDate={dateFrom ?? undefined}
          popoverProps={{
            zIndex: Z_INDEX_MOBILE_MODAL_DATEPICKER,
            withinPortal: true,
          }}
        />

        <Select
          className={styles.fieldWide}
          label={t('notes.filters.hasLocation')}
          data={locationOptions}
          value={locationValue}
          onChange={handleLocationChange}
          leftSection={<IconMapPin size={ICON_SIZE_SM} />}
          allowDeselect={false}
        />

        {hasLocation === true && (
          <Autocomplete
            className={styles.fieldWide}
            label={t('notes.filters.location')}
            placeholder={t('notes.filters.locationPlaceholder')}
            data={uniqueNoteLocations}
            value={localLocation}
            onChange={handleLocationFilterChange}
            leftSection={<IconMapPin size={ICON_SIZE_SM} />}
            clearable
            limit={10}
          />
        )}

        {showSort && (
          <SortControls
            options={NOTE_SORT_OPTIONS}
            sortBy={sortBy}
            sortDescending={sortDescending}
            onSortByChange={handleSortByChange}
            onSortDescendingChange={setSortDescending}
          />
        )}
      </div>

      {hasActiveFilters && (
        <div
          className={`${styles.clearRow} ${isMapPanel ? styles.clearRowCentered : ''}`}
        >
          <ClearFiltersButton onClick={resetFilters} />
        </div>
      )}
    </div>
  );
});
